import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import toast from "react-hot-toast";
import Dialog from "../../components/UI/Dialog";
import { ConfirmDialog } from "../../components/UI";
import { useAuthStore } from "../../store/authStore";
import { useLanguage } from "../../contexts/LanguageContext";
import { useElementWidth } from "../../hooks/useElementWidth";
import { Page, request, usePoll, steamLogin } from "./shared";
import History from "./History";
import Stage, { Party } from "./home/Stage";
import Social, { InviteCtx, Person } from "./home/Social";
import ReferralBanner from "./home/ReferralBanner";

/* Панель друзей рядом с игровой зоной только при достаточной ширине контента (design-spec 06). */
const Layout = styled.div<{ $wide: boolean }>`
  display: grid;
  grid-template-columns: ${(p) => (p.$wide ? "minmax(0, 1fr) 320px" : "minmax(0, 1fr)")};
  gap: 24px;
  align-items: start;
  > * {
    min-width: 0;
  }
`;
/* Друзья и реферальная плашка: на широком экране колонка липнет к верху, список друзей прокручивается внутри. */
const Side = styled.div`
  display: grid;
  gap: 16px;
  align-content: start;
  min-width: 0;
  @media (min-width: 1040px) {
    position: sticky;
    top: 72px;
    > aside:first-child {
      position: static;
      max-height: calc(100svh - 330px);
      min-height: 200px;
    }
  }
`;
const Main = styled.div`
  display: grid;
  gap: 32px;
  min-width: 0;
`;
const Free = styled.p`
  font-size: 14px;
  line-height: 20px;
  color: ${(p) => p.theme.colors.textSecondary};
`;

export default function Play() {
  const { t } = useLanguage();
  const user = useAuthStore((s) => s.user),
    nav = useNavigate();
  const [now, setNow] = useState(Date.now()),
    [busy, setBusy] = useState(false),
    [q, setQ] = useState(""),
    [found, setFound] = useState<Person[]>([]),
    [searching, setSearching] = useState(false),
    [remove, setRemove] = useState(""),
    [inviteOpen, setInviteOpen] = useState(false),
    [pendingAction, setPendingAction] = useState(""),
    [syncError, setSyncError] = useState(""),
    [friendsOpen, setFriendsOpen] = useState(false),
    [invited, setInvited] = useState<Set<string>>(new Set());
  const [rootRef, width] = useElementWidth<HTMLDivElement>();
  const wide = width >= 1040;
  const actionLock = useRef(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const {
    data: party,
    error,
    setData: setParty,
    reload: reloadParty,
  } = usePoll<Party>("/play/party", 3000, busy);
  useEffect(() => {
    if (party) setSyncError("");
  }, [party]);
  const {
    data: friends,
    error: friendsError,
    setData: setFriends,
    reload: reloadFriends,
  } = usePoll<{ items: Person[] }>("/friends", 10000);
  const { data: requests, setData: setRequests } = usePoll<{
    incoming: Person[];
    outgoing: Person[];
  }>("/friends/requests", 10000);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    if (party?.matchId) nav("/match/" + party.matchId);
  }, [party?.matchId, nav]);
  useEffect(() => {
    let alive = true;
    if (!q.trim()) {
      setFound([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const timer = setTimeout(() => {
      request("/users/search?q=" + encodeURIComponent(q))
        .then((d) => {
          if (alive) setFound(d.items || []);
        })
        .catch(() => {})
        .finally(() => {
          if (alive) setSearching(false);
        });
    }, 300);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [q]);

  async function action(body: { action: string; [key: string]: unknown }) {
    if (actionLock.current || syncError || error) return false;
    actionLock.current = true;
    setPendingAction(body.action);
    setBusy(true);
    let ok = true;
    try {
      await request("/play/party", body);
    } catch (e) {
      ok = false;
      toast.error((e as Error).message);
    } finally {
      // Keep actions locked until the server state replaces the old button.
      // Polling is paused during the mutation, so a stale response cannot undo it.
      try {
        setParty(await request<Party>("/play/party"));
      } catch {
        setSyncError(t("home.sync_error"));
      }
      setBusy(false);
      setPendingAction("");
      actionLock.current = false;
    }
    return ok;
  }
  async function friendAction(path: string, body = {}) {
    try {
      await request(path, body);
      toast.success(t("common.saved"));
      return true;
    } catch (e) {
      toast.error((e as Error).message);
      return false;
    }
  }
  async function invite(id: string) {
    if (!(await action({ action: "invite", userId: id }))) return;
    setInvited((s) => new Set(s).add(id));
    setTimeout(
      () =>
        setInvited((s) => {
          const n = new Set(s);
          n.delete(id);
          return n;
        }),
      8000,
    );
  }
  async function addFriend(id: string) {
    if (await friendAction("/friends/requests", { userId: id }))
      setFound((list) =>
        list.map((f) => (f.id === id ? { ...f, relation: "pending_out" } : f)),
      );
  }
  async function answer(id: string, verb: "accept" | "decline") {
    const from = requests?.incoming.find((x) => x.id === id);
    if (!(await friendAction("/friends/requests/" + id + "/" + verb))) return;
    setRequests((d) =>
      d ? { ...d, incoming: d.incoming.filter((x) => x.id !== id) } : d,
    );
    if (verb === "accept" && from)
      setFriends((d) => {
        const uid = from.user_id || from.id;
        if (!d || d.items.some((x) => x.id === uid)) return d;
        return { ...d, items: [...d.items, { ...from, id: uid, status: "offline" }] };
      });
  }

  const queued = !!party?.queuedAt;
  const memberCount = party?.members.length || 1;
  const full = memberCount >= 5;
  const locked = busy || !!syncError || !!error;
  const inviteBlocked = locked || queued || full;
  const ctx: InviteCtx = {
    partyIds: new Set(party?.members.map((m) => m.id) || []),
    invited,
    full,
    searching: queued,
    locked,
  };
  const sorted = (friends?.items || []).filter((f) => ["online", "in_game"].includes(f.status || ""));
  const retry = () => {
    setSyncError("");
    reloadParty();
  };
  const social = (sheet: boolean) => (
    <Social
      sheet={sheet}
      friends={friends ? friends.items : null}
      friendsError={friendsError}
      incoming={requests?.incoming || []}
      q={q}
      setQ={setQ}
      found={found}
      searching={searching}
      ctx={ctx}
      searchRef={sheet ? undefined : searchRef}
      onFocusSearch={() => searchRef.current?.focus()}
      onRetry={reloadFriends}
      onAdd={addFriend}
      onInvite={invite}
      onRemove={setRemove}
      onAccept={(id) => answer(id, "accept")}
      onDecline={(id) => answer(id, "decline")}
    />
  );

  return (
    <Page>
      <div ref={rootRef}>
        <Layout $wide={wide}>
          <Main>
            <Stage
              user={user}
              party={party}
              now={now}
              locked={locked}
              busy={busy}
              pendingAction={pendingAction}
              syncError={syncError}
              error={error}
              inviteBlocked={inviteBlocked}
              showFriendsButton={!wide}
              friendsOnline={friends ? sorted.length : null}
              onAction={action}
              onInvite={() => setInviteOpen(true)}
              onLinkSteam={() => steamLogin().catch((e) => toast.error(e.message))}
              onRetry={retry}
              onOpenFriends={() => setFriendsOpen(true)}
            />
            {!wide && <ReferralBanner />}
            <History />
          </Main>
          {wide && (
            <Side>
              {social(false)}
              <ReferralBanner />
            </Side>
          )}
        </Layout>
      </div>

      {(inviteOpen || friendsOpen) && (
        <Dialog
          size="md"
          label={inviteOpen ? t("home.invite_player") : t("home.friends_sheet")}
          onClose={() => {
            setInviteOpen(false);
            setFriendsOpen(false);
          }}
        >
          <h2>{inviteOpen ? t("home.invite_player") : t("home.friends_sheet")}</h2>
          {inviteOpen && <Free>{full ? t("home.party_full") : t("home.invite_slots", { n: 5 - memberCount })}</Free>}
          {social(true)}
        </Dialog>
      )}

      {remove && (
        <ConfirmDialog
          title={t("home.remove_title_named", { name: friends?.items.find((x) => x.id === remove)?.username || "" })}
          description={t("home.remove_text")}
          confirmLabel={t("home.remove_friend")}
          cancelLabel={t("home.keep")}
          onClose={() => setRemove("")}
          onConfirm={() =>
            request("/friends/" + remove, undefined, "DELETE")
              .then(() => {
                setFriends((d) => (d ? { ...d, items: d.items.filter((x) => x.id !== remove) } : d));
                setRemove("");
                toast.success(t("home.removed"));
              })
              .catch((e) => toast.error(e.message))
          }
        />
      )}
    </Page>
  );
}
